import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  Pencil,
  Trash,
  ArrowClockwise,
  CheckCircle,
  Prohibit,
  Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { detectColumns, safeInsert, safeUpdate } from "@/lib/schema";

const EMPTY = {
  id: null,
  question: "",
  answer: "",
  category: "",
  display_order: 0,
  sort_order: 0,
  is_active: true,
};

export default function AdminFaqs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("faqs")
      .select("*")
      .order("display_order", { ascending: true, nullsFirst: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("admin-faqs")
      .on("postgres_changes", { event: "*", schema: "public", table: "faqs" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  const categories = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.category && set.add(r.category));
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((f) => {
      const isActive = f.is_active !== false;
      if (statusFilter === "active" && !isActive) return false;
      if (statusFilter === "disabled" && isActive) return false;
      if (categoryFilter !== "all" && (f.category || "") !== categoryFilter) return false;
      if (!query) return true;
      return `${f.question || ""} ${f.answer || ""}`.toLowerCase().includes(query);
    });
  }, [rows, q, statusFilter, categoryFilter]);

  const openNew = () => setEditing({ ...EMPTY, display_order: rows.length + 1, sort_order: rows.length + 1 });
  const openEdit = (f) => setEditing({ ...EMPTY, ...f });

  const persist = async (payload) => {
    setSaving(true);
    try {
      const record = {
        question: payload.question,
        answer: payload.answer,
        category: payload.category,
        display_order: Number(payload.display_order) || 0,
        sort_order: Number(payload.sort_order) || Number(payload.display_order) || 0,
        is_active: payload.is_active !== false,
      };
      let res;
      if (payload.id) {
        res = await safeUpdate("faqs", record, { id: payload.id });
      } else {
        res = await safeInsert("faqs", record);
      }
      if (res.error) throw res.error;
      toast.success(payload.id ? "FAQ updated" : "FAQ created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.question}"?`)) return;
    const { error } = await supabase.from("faqs").delete().eq("id", f.id);
    if (error) toast.error(error.message);
    else {
      toast.success("FAQ deleted");
      load();
    }
  };

  const toggleActive = async (f) => {
    const next = !(f.is_active !== false);
    const cols = await detectColumns("faqs");
    if (!cols.has("is_active") && cols.size > 0) {
      toast.error("Your faqs table has no is_active column — can't toggle status.");
      return;
    }
    const { error } = await supabase.from("faqs").update({ is_active: next }).eq("id", f.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "FAQ enabled" : "FAQ disabled");
      load();
    }
  };

  const duplicate = async (f) => {
    const { id, created_at, ...rest } = f;
    void id;
    void created_at;
    const record = {
      ...rest,
      question: `${f.question} (copy)`,
      display_order: (f.display_order || 0) + 1,
      sort_order: (f.sort_order || 0) + 1,
    };
    const { error } = await safeInsert("faqs", record, { single: false });
    if (error) toast.error(error.message);
    else {
      toast.success("FAQ duplicated");
      load();
    }
  };

  const move = async (f, delta) => {
    const cols = await detectColumns("faqs");
    if (!cols.has("display_order") && !cols.has("sort_order")) {
      toast.error("Your faqs table has no ordering column.");
      return;
    }
    const orderCol = cols.has("display_order") ? "display_order" : "sort_order";
    const sorted = [...rows].sort((a, b) => (a[orderCol] || 0) - (b[orderCol] || 0));
    const idx = sorted.findIndex((r) => r.id === f.id);
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= sorted.length) return;
    const neighbour = sorted[nextIdx];
    const a = f[orderCol] || 0;
    const b = neighbour[orderCol] || 0;
    const patchA = { [orderCol]: b };
    const patchB = { [orderCol]: a };
    if (cols.has("sort_order") && orderCol !== "sort_order") {
      patchA.sort_order = b;
      patchB.sort_order = a;
    }
    await Promise.all([
      supabase.from("faqs").update(patchA).eq("id", f.id),
      supabase.from("faqs").update(patchB).eq("id", neighbour.id),
    ]);
    load();
  };

  return (
    <AdminLayout title="FAQs">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="faqs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search question or answer…"
              className="pl-9 h-10 rounded-xl bg-white"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="faqs-category-filter" className="w-44 h-10 rounded-xl bg-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="faqs-status-filter" className="w-40 h-10 rounded-xl bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="faqs-refresh" className="rounded-xl h-10 border-slate-200">
            <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
            Refresh
          </Button>
          <Button onClick={openNew} data-testid="faqs-add" className="rounded-xl h-10 bg-navy-deep hover:bg-navy text-white">
            <Plus size={14} weight="bold" className="mr-1.5" />
            Add FAQ
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No FAQs match your filters. Click <b>Add FAQ</b> to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold w-20">Order</th>
                  <th className="text-left px-5 py-3 font-semibold">Question</th>
                  <th className="text-left px-5 py-3 font-semibold">Answer</th>
                  <th className="text-left px-5 py-3 font-semibold">Category</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Created</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f, i) => {
                  const active = f.is_active !== false;
                  return (
                    <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3">
                        <div className="inline-flex flex-col items-center gap-0.5">
                          <button
                            onClick={() => move(f, -1)}
                            disabled={i === 0}
                            className="h-5 leading-none text-slate-400 hover:text-cyan-brand disabled:opacity-30"
                            title="Move up"
                            data-testid={`faq-up-${f.id}`}
                          >
                            ▲
                          </button>
                          <span className="font-mono-data text-[12px] text-slate-500 tabular-nums">
                            {f.display_order ?? f.sort_order ?? i + 1}
                          </span>
                          <button
                            onClick={() => move(f, 1)}
                            disabled={i === filtered.length - 1}
                            className="h-5 leading-none text-slate-400 hover:text-cyan-brand disabled:opacity-30"
                            title="Move down"
                            data-testid={`faq-down-${f.id}`}
                          >
                            ▼
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-navy-deep font-semibold max-w-sm">{f.question}</td>
                      <td className="px-5 py-3 text-slate-600 max-w-md truncate">{f.answer}</td>
                      <td className="px-5 py-3 text-slate-600">{f.category || "—"}</td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${
                            active
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {active ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-[12px]">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => toggleActive(f)}
                            className={`inline-flex items-center h-8 w-8 rounded-lg justify-center ${
                              active
                                ? "text-slate-500 hover:text-red-500 hover:bg-red-50"
                                : "text-emerald-600 hover:bg-emerald-50"
                            }`}
                            title={active ? "Disable" : "Enable"}
                            data-testid={`faq-toggle-${f.id}`}
                          >
                            {active ? <Prohibit size={14} /> : <CheckCircle size={14} />}
                          </button>
                          <button
                            onClick={() => duplicate(f)}
                            className="inline-flex items-center h-8 w-8 rounded-lg text-slate-500 hover:text-cyan-brand hover:bg-slate-100 justify-center"
                            title="Duplicate"
                            data-testid={`faq-duplicate-${f.id}`}
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => openEdit(f)}
                            className="inline-flex items-center h-8 w-8 rounded-lg text-slate-500 hover:text-cyan-brand hover:bg-slate-100 justify-center"
                            title="Edit"
                            data-testid={`faq-edit-${f.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <FaqEditor
          value={editing}
          onChange={setEditing}
          onSave={() => persist(editing)}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}
    </AdminLayout>
  );
}

function FaqEditor({ value, onChange, onSave, onCancel, saving }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const isEdit = !!value.id;

  const submit = (e) => {
    e.preventDefault();
    if (!value.question.trim()) return toast.error("Question is required");
    if (!value.answer.trim()) return toast.error("Answer is required");
    onSave();
  };

  return (
    <Dialog open onOpenChange={(o) => (o ? null : onCancel())}>
      <DialogContent data-testid="faq-editor" className="max-w-2xl p-0">
        <DialogHeader className="p-6 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl text-navy-deep">
            {isEdit ? "Edit FAQ" : "New FAQ"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">
              Question <span className="text-red-500">*</span>
            </Label>
            <Input
              data-testid="faq-question"
              value={value.question}
              onChange={(e) => set({ question: e.target.value })}
              required
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">
              Answer <span className="text-red-500">*</span>
            </Label>
            <Textarea
              data-testid="faq-answer"
              value={value.answer}
              onChange={(e) => set({ answer: e.target.value })}
              rows={5}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Category</Label>
              <Input
                data-testid="faq-category"
                value={value.category || ""}
                onChange={(e) => set({ category: e.target.value })}
                className="mt-1.5 h-11 rounded-xl"
                placeholder="Shipping"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Display order</Label>
              <Input
                data-testid="faq-order"
                type="number"
                value={value.display_order}
                onChange={(e) =>
                  set({ display_order: Number(e.target.value), sort_order: Number(e.target.value) })
                }
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-slate-200 cursor-pointer w-full">
                <input
                  type="checkbox"
                  data-testid="faq-active"
                  checked={value.is_active !== false}
                  onChange={(e) => set({ is_active: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-[13px] font-semibold text-navy-deep">Active</span>
              </label>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="faq-cancel"
              className="rounded-full h-11 px-5 border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="faq-save"
              className="rounded-full h-11 px-5 bg-cta-gradient text-white font-semibold"
            >
              {saving ? "Saving…" : isEdit ? "Update" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
